/**
 * StockDataPlansPage - Planos de dados (Estoque › Gestão de Chip)
 *
 * Lista paginada via `get_stock_data_plans`; cadastro em StockDataPlanFormDialog.
 *
 * RELEASE: 2026-10-09 v1.1.0 - Filtros rápidos no estilo pill padrão (QUICK_FILTER_PILL) e busca com lupa.
 * RELEASE: 2026-10-07 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { MoreHorizontal, Pencil, Plus, Power, Signal, Search } from 'lucide-react';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  ListPageLayout, ListPageHeader, ListPageFiltersBar, ListPageContent,
  LIST_FILTERS_INPUT_CLASS, LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import {
  dataPlanCarrierLabel, formatPackageMb, useStockDataPlans, useStockRegistryMutations, type StockDataPlan, type StockListFilters,
} from '@/hooks/useStockRegistries';
import { StockDataPlanFormDialog } from '@/components/stock/StockDataPlanFormDialog';

const brl = (n: number | null) => (n === null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

export default function StockDataPlansPage() {
  const { canCreate, canEdit, isAdmin } = usePermissionsContext();
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<StockListFilters>({ search: '', status: 'active', page: 1, pageSize: 25 });
  const [editor, setEditor] = useState<{ open: boolean; item: StockDataPlan | null }>({ open: false, item: null });
  const { data, isLoading } = useStockDataPlans(filters);
  const { upsertDataPlan } = useStockRegistryMutations();

  // Busca com atraso para não consultar a cada tecla
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim(), page: 1 })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const allowCreate = isAdmin || canCreate('stock_data_plans');
  const allowEdit = isAdmin || canEdit('stock_data_plans');
  const items = data?.items ?? [];

  const toggleActive = (p: StockDataPlan) => {
    const { id, supplier_name: _s, ...rest } = p;
    upsertDataPlan.mutate({ id, data: { ...rest, is_active: !p.is_active } });
  };

  return (
    <ListPageLayout>
      <ListPageHeader title="Planos de dados"
        actions={allowCreate ? (
          <Button size="xs" onClick={() => setEditor({ open: true, item: null })}><Plus className="mr-1.5 h-4 w-4" /> Novo plano</Button>
        ) : undefined} />
      <ListPageFiltersBar>
        {/* Busca com lupa, mesmo padrão da listagem de Propostas */}
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground sm:h-3.5 sm:w-3.5" />
          <Input className={`${LIST_FILTERS_INPUT_CLASS} w-full pl-8`} value={searchInput} maxLength={100}
          placeholder="Buscar por nome ou fornecedor..." onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v as StockListFilters['status'], page: 1 }))}>
          <SelectTrigger className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} ${QUICK_FILTER_PILL} shrink-0`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>
      </ListPageFiltersBar>
      <ListPageContent>
        {isLoading ? <Skeleton className="h-40 w-full rounded-lg" /> : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Signal className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nenhum plano de dados encontrado.</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Operadoras</TableHead>
                  <TableHead>Pacote (MB)</TableHead>
                  <TableHead>Valor negociado</TableHead>
                  <TableHead className="w-24">Vencimento</TableHead>
                  <TableHead className="w-24">Situação</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => allowEdit && setEditor({ open: true, item: p })}>
                    <TableCell>
                      <div className="font-medium">{p.name}</div>
                      {p.supplier_name && <div className="text-xs text-muted-foreground">{p.supplier_name}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {p.compatible_carriers.map((c) => <Badge key={c} variant="secondary">{dataPlanCarrierLabel(c)}</Badge>)}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{formatPackageMb(p.data_package_mb)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{brl(p.negotiated_amount)}</TableCell>
                    <TableCell className="text-sm">{p.due_day ? `Dia ${p.due_day}` : '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider">
                        <StatusDot tone={p.is_active ? 'emerald' : 'muted'} />{p.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {allowEdit && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditor({ open: true, item: p })}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleActive(p)}><Power className="mr-2 h-4 w-4" /> {p.is_active ? 'Inativar' : 'Ativar'}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          onPageSizeChange={(pageSize) => setFilters((f) => ({ ...f, pageSize, page: 1 }))}
          pageSizeOptions={[10, 25, 50, 100]} />
      </ListPageContent>
      <StockDataPlanFormDialog open={editor.open} plan={editor.item} onOpenChange={(open) => setEditor((c) => ({ ...c, open }))} />
    </ListPageLayout>
  );
}
